import { Controller } from "@hotwired/stimulus";
import { createConsumer } from "@rails/actioncable";
import { FetchRequest } from "@rails/request.js";
import Backup from "./backup";

export default class extends Controller {
  static outlets = ["store-audio--recorder"]
  static values = {
    consultationId: Number,
    path: String,
    preventTexts: Object,
    startOnConnect: {
      type: Boolean,
      default: false
    }
  }

  async connect() {
    if (Turbo.session.recentRequests.size === 0) {
      Turbo.session.recentRequests.add(crypto.randomUUID());
    }
    const turboRequestIds = Array.from(Turbo.session.recentRequests)
    this.turboRequestId = turboRequestIds.at(-1);
    this.isSubscribed = false;
    this.backup = new Backup(this.consultationIdValue);

    if (this.startOnConnectValue) {
      await this.start()
      this.preventLinks()
    }
  }

  disconnect() {
    this.stream?.getTracks().forEach(t => t.stop());
    if (this.consumer) {
      this.#removeSubscription();
    }
  }

  preventLinks() {
    document.querySelectorAll("a").forEach((link) => {
      if (link.dataset.turboConfirm) return;

      link.dataset.turboMethod = "get";
      link.dataset.turboConfirm = JSON.stringify(this.preventTextsValue);
    })
    this.dispatch("prevent-backup")
  }

  async preventExit(event) {
    if (!this.isSubscribed) return;

    this.consumer.subscriptions.remove(this.subscription);
    this.isSubscribed = false;
    event.preventDefault();
    event.returnValue = "";
    const backupBlob = this.storeAudioRecorderOutlet.backupBlob
    await this.backup.injectBlob(backupBlob);

    window.addEventListener("mousemove", () => {
      if (!this.isSubscribed) {
        this.isSubscribed = true;
        setTimeout(() => {
          this.#updateRecording("recording");
          this.subscription = this.consumer.subscriptions.create(this.channel, {
            received: this.handleMessage.bind(this),
          });
        }, 30000);
      }
    }, { once: true });
  }

  async start() {
    const preparation = await this.storeAudioRecorderOutlet.prepare();
    if (preparation === "error") return;

    this.stream = this.storeAudioRecorderOutlet.stream;
    this.storeAudioRecorderOutlet.start();
    this.element.dispatchEvent(new CustomEvent("transcript:start", { bubbles: true }));

    this.#triggerDispatches()

    this.#createRecording();

    this.consumer = createConsumer();
    this.channel = {
      channel: "RecordingChannel",
      consultation_id: this.consultationIdValue,
      turbo_request_id: this.turboRequestId,
    };
    this.subscription = this.consumer.subscriptions.create(this.channel, {
      received: this.handleMessage.bind(this),
    });

    this.isSubscribed = true;
  }

  async resume() {
    this.storeAudioRecorderOutlet.resume();
    await this.#updateRecording("recording");
  }

  async pause() {
    this.storeAudioRecorderOutlet.pause();
    await this.#updateRecording("paused");
  }

  stop() {
    this.element.dispatchEvent(new CustomEvent("transcript:stop", { bubbles: true }));
    this.storeAudioRecorderOutlet.stop();
    this.#removeSubscription();
  }

  async handleMessage(data) {
    if (data.force_pause) {
      this.pause();
      this.#triggerRecording("paused")
      return;
    }

    if (data.force_stop) {
      this.stop();
      this.#triggerRecording("stoped")
      return;
    }

    if (data.unsubscribe) {
      this.consumer.subscriptions.remove(this.subscription);
      return;
    }
  }

  async #createRecording() {
    const formData = new FormData();
    formData.append('turbo_request_id', this.turboRequestId);
    const request = new FetchRequest(
      "post", this.pathValue, { body: formData }
    );
    const response = await request.perform();

    const data = await response.json;
    this.recordingId = data.recording_id
  }

  async #updateRecording(status) {
    const formData = new FormData();
    formData.append('status', status);
    formData.append('turbo_request_id', this.turboRequestId);
    formData.append('recording_id', this.recordingId);
    const request = new FetchRequest(
      "patch", this.pathValue, { body: formData }
    );
    await request.perform();
  }

  #removeSubscription() {
    this.consumer.subscriptions.remove(this.subscription);
    this.isSubscribed = false;
  }

  #triggerDispatches() {
    this.#triggerRecording("started")
    this.element.dispatchEvent(new CustomEvent("modal:lock"));
    this.element.dispatchEvent(
      new CustomEvent("modal:preventexit", {
        detail: this.preventTextsValue
      })
    );
    document.dispatchEvent(new CustomEvent("vetomatic:record-started"));
  }

  #triggerRecording(status) {
    this.element.dispatchEvent(
      new CustomEvent("recording", {
        detail: { status }
      })
    );
  }
}
